<template>
  <long-query-layout
    :resource-name="resourceName"
    :query-filter="queryFilter"
    :map-query-filter-for-api="mapQueryFilterForApi"
    :apply-query-filter="applyQueryFilter"
    :active-tab.sync="activeTab"
    :customers.sync="customerCollection"
    :excel-properties-handler="excelPropertiesHandler.bind(this)"
    :customer-download-only="true"
    @query-completed="loadData"
    @result-area-size-change="updateTableSize"
    @query-id-change="queryId = $event"
  >
    <!-- -------------------------------------------------------- -->
    <!-- ヘッダのアクション -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:header-actions="slotProps">
      <div>
        <gl-button @click="editReport = true">採用順</gl-button>
      </div>
      <div>
        <gl-switch
          :value="isDetail"
          :disabled="!(slotProps.queryId && activeTab == 'result')"
          @input="toggleDetail"
          >詳細</gl-switch
        >
      </div>
    </template>

    <!-- -------------------------------------------------------- -->
    <!-- 分析条件 -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:filter>
      <!-- 期間 -->
      <query-filter-element id="term" title="期間" contentWidth="medium">
        <filter-term v-bind.sync="queryFilter.term" :default-week-count="26" />
      </query-filter-element>

      <!-- 商品分類 -->
      <query-filter-element id="item-tapir-mk" title="商品分類">
        <filter-item v-bind.sync="queryFilter.item" />
      </query-filter-element>

      <!-- 店舗 -->
      <query-filter-element id="store" title="店舗" contentWidth="medium">
        <filter-store v-bind.sync="queryFilter.store" />
      </query-filter-element>

      <!-- 顧客グループ -->
      <!-- TODO Kappa側で顧客選択の実装ができるまで顧客指定はしません -->
      <!-- <query-filter-element id="customer" title="顧客グループ">
        <filter-customer v-bind.sync="queryFilter.customer" />
      </query-filter-element> -->

      <!-- オプション -->
      <query-filter-element id="option-tapir-mk" title="オプション">
        <filter-option v-bind.sync="queryFilter.option" />
      </query-filter-element>

      <md-dialog
        :md-active.sync="uploadingItemFile"
        :md-close-on-esc="false"
        :md-click-outside-to-close="false"
      >
        <md-dialog-content>
          <div style="display:flex;align-items:center">
            <spinner :diameter="30" />
            <span style="margin-left:var(--spacing-medium)">
              階層ファイルをアップロードしています...
            </span>
          </div>
        </md-dialog-content>
      </md-dialog>
    </template>

    <!-- -------------------------------------------------------- -->
    <!-- 分析結果 -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:result>
      <md-tabs ref="myMdTabs">
        <md-tab md-label="明細（商品）">
          <gl-table ref="itemTable" v-bind="resultTable.item" />
        </md-tab>
        <md-tab md-label="サマリ（セグメントF）">
          <gl-table ref="segmentFTable" v-bind="resultTable.segmentF" />
        </md-tab>
        <md-tab md-label="サマリ（セグメントN）">
          <gl-table ref="segmentNTable" v-bind="resultTable.segmentN" />
        </md-tab>
        <md-tab md-label="ダウンロード">
          <div>
            <a
              v-if="dendroImageUrl"
              :href="dendroImageUrl"
              target="_blank"
              rel="noopener noreferrer"
            >
              デンドログラム画像
            </a>
            <span v-else-if="noImageReason">{{ noImageReason }}</span>
            <span v-else>ロード中…</span>
          </div>
          <div>
            <a
              v-if="distMatrixUrl"
              :href="distMatrixUrl"
              target="_blank"
              rel="noopener noreferrer"
            >
              距離行列表CSV
            </a>
            <span v-else>ロード中…</span>
          </div>
          <div>
            <a
              v-if="distMatrixUrlSjis"
              :href="distMatrixUrlSjis"
              target="_blank"
              rel="noopener noreferrer"
            >
              距離行列表CSV
              (S-JIS版。Excelなどで文字化けする場合はこちらをご利用ください。)
            </a>
            <span v-else>ロード中…</span>
          </div>
        </md-tab>
      </md-tabs>

      <md-dialog
        :md-active.sync="editReport"
        :md-close-on-esc="true"
        :md-click-outside-to-close="true"
      >
        <md-dialog-content>
          <h4>
            採用順
          </h4>
          <md-field class="input-inline__field input-inline__field--large">
            <md-select v-model="queryFilter.option.adoptOrder">
              <md-option value="id_count">ID数</md-option>
              <md-option value="coefficient_id_count">加重平均ID数</md-option>
            </md-select>
          </md-field>

          <h4>
            採用順赤フォント
          </h4>
          <div
            class="input-inline input-inline"
            style="margin-right: var(--spacing-large);"
          >
            <label class="input-inline__label" style="margin-right: 0;">
              ID数構成比（非加重平均）
            </label>
            <md-field class="input-inline__field input-inline__field--small">
              <md-input
                v-model.number="customerCountRatioBorder"
                type="number"
                min="0"
              ></md-input>
            </md-field>
            <label class="input-inline__label" style="margin-left: 0;">
              ％未満の商品
            </label>
          </div>
          <div>
            <gl-button primary raised @click="reloadItemGrid">
              反映
            </gl-button>
          </div>
        </md-dialog-content>
      </md-dialog>
    </template>
  </long-query-layout>
</template>

<script>
// 分析条件, 分析結果のためのレイアウト
import LongQueryLayout from '@/components/layout/LongQueryLayout.vue'
// 分析条件
import QueryFilterElement from '@/components/QueryFilterElement.vue'
import FilterTerm from '@/components/FilterTerm.vue'
import FilterItem from '@/components/FilterItemTapirMk.vue'
import FilterStore from '@/components/FilterStore.vue'
import FilterOption from '@/components/FilterOptionTapirMk.vue'
// 分析条件モデル
import term from '@/queryfilter/term'
import item from '@/queryfilter/itemTapirMk'
import store from '@/queryfilter/store'
// 分析結果
import GlTable from '@/components/GlTable.vue'
// misc
import Spinner from '@/components/Spinner.vue'

import { trimAll } from '@/util/string'
import repository from '@/repositories/Repository'
import { searchCellCustomFunction } from '@/util/custom_formatters'

let sizeFixed = false

let hold = false

const ALWAYS_SHOW_ITEM_COLUMNS = [
  'segment_f',
  'segment_f_customer_count',
  'segment_n',
  'segment_n_customer_count',
  'relation_order',
  'code',
  'name',
  'customer_count',
  'customer_count_order',
  'coefficient_customer_count_order',
  'recommend_by_customer_count',
  'recommend_by_coefficient_customer_count',
  'customer_count_composition_ratio',
  'customer_count_ratio',
  'qty_composition_ratio',
  'amount_per_qty',
]

const ALWAYS_SHOW_SEGMENT_COLUMNS = [
  'segment_f',
  'segment_n',
  'customer_count',
  'customer_count_ratio',
  'amount_per_qty',
  'sku_count',
  'amount_per_sku',
  // 年代構成比列は動的なので列構成をAPIから取得してから作成
]

// 全列共通セル背景色・文字色フォーマッター
function attributes(rowData, cell, value, colName) {
  const bgColors = rowData.attributes ? rowData.attributes.bgColor : {}
  const bgColor = bgColors ? bgColors[colName] : undefined
  if (bgColor) {
    cell.style.backgroundColor = bgColor
  }

  const fgColors = rowData.attributes ? rowData.attributes.fgColor : {}
  const fgColor = fgColors ? fgColors[colName] : undefined
  if (fgColor) {
    cell.style.color = fgColor
  }
}

function toggleDetail(alwaysShowColumns, colModelsRef, isDetail) {
  colModelsRef.forEach(c => {
    if (!alwaysShowColumns.includes(c.colName)) {
      c.hidden = !isDetail
    }
  })
}

/**
 * 採用順切替（ID数<->加重平均ID数）時の列表示非表示設定
 */
function switchAdoptOrder(newOrder, colModelsRef) {
  let states
  if (newOrder === 'id_count') {
    states = {
      customer_count_order: false,
      coefficient_customer_count_order: true,
      recommend_by_customer_count: false,
      recommend_by_coefficient_customer_count: true,
      customer_count_ratio: false,
      customer_count_composition_ratio: true,
    }
  } else if (newOrder === 'coefficient_id_count') {
    states = {
      customer_count_order: true,
      coefficient_customer_count_order: false,
      recommend_by_customer_count: true,
      recommend_by_coefficient_customer_count: false,
      customer_count_ratio: true,
      customer_count_composition_ratio: false,
    }
  }

  const keys = new Set(Object.keys(states))
  colModelsRef.forEach(c => {
    if (keys.has(c.colName)) {
      c.hidden = states[c.colName]
    }
  })
}

export default {
  components: {
    QueryFilterElement,
    FilterTerm,
    FilterItem,
    FilterStore,
    FilterOption,
    LongQueryLayout,
    GlTable,
    Spinner,
  },
  data() {
    return {
      resourceName: 'tapir-mk',
      /**
       * 表示タブ（分析条件、分析結果）
       *
       * 表示タブをプログラム側から切り替えたい場合をこれを使います。
       */
      activeTab: undefined,
      /**
       * 現在表示しているクエリID
       */
      queryId: undefined,
      /**
       * 分析条件
       */
      queryFilter: {
        term: { order: 1, ...term.get() },
        item: { order: 2, ...item.get(), summaryKey: 'itemTapirMk' },
        store: { order: 3, ...store.get() },
        option: {
          order: 4,
          summaryKey: 'optionTapirMk',
          report: 'all',
          orderValue: 100,
          customerCountValue: 100,
          adoptOrder: 'id_count',
          repeat: 2,
          errors: [],
        },
      },
      /**
       * 分析結果
       */
      resultTable: {
        item: {
          columns: undefined,
          groupHeaders: undefined,
          sortColumnName: 'relation_order',
          sortOrder: 'asc',
          url: undefined,
          width: 0,
          height: 0,
          customFormatter: {
            linkify: (rowData, cell, value, colName) => {
              cell.classList.add('customer-collection-cell')
              cell.setAttribute(
                'data-customer-collection',
                rowData.customer_filter[colName],
              )

              const toggle = e => {
                if (!this.resultTable.item.columns) {
                  throw new Error('Column not found')
                }

                const id = e.target.getAttribute('data-customer-collection')
                let text

                if (colName.startsWith('segment_f')) {
                  const name = rowData.segment_f || '合計'

                  if (colName.endsWith('_r')) {
                    text = `${name} - R`
                  } else if (colName.endsWith('_t')) {
                    text = `${name} - T`
                  } else {
                    text = name
                  }
                } else if (colName.startsWith('segment_n')) {
                  const name = rowData.segment_f || '合計'

                  if (colName.endsWith('_r')) {
                    text = `${name} - R`
                  } else if (colName.endsWith('_t')) {
                    text = `${name} - T`
                  } else {
                    text = name
                  }
                } else if (colName.startsWith('customer_count')) {
                  const name =
                    rowData.code != null ? trimAll(rowData.name) : '合計'

                  if (colName.endsWith('_r')) {
                    text = `${name} - R`
                  } else if (colName.endsWith('_t')) {
                    text = `${name} - T`
                  } else {
                    text = name
                  }
                }

                const customer = { id, text }

                this.$emit('toggle-customer-collection', customer)
              }

              cell.addEventListener('mousedown', e => {
                hold = true
                document.body.classList.add('no-select')
                toggle(e)
              })
              cell.addEventListener('mouseup', () => {
                hold = false
                document.body.classList.remove('no-select')
              })

              cell.addEventListener('mouseenter', e => {
                if (colName.startsWith('segment')) {
                  return
                }
                if (!hold) {
                  return
                }
                toggle(e)
              })
            },
            linkifySearch: searchCellCustomFunction,
          },
        },
        segmentF: {
          columns: undefined,
          groupHeaders: undefined,
          sortColumnName: 'segment_f',
          sortOrder: 'asc',
          url: undefined,
          width: 0,
          height: 0,
          customFormatter: {
            linkify: (rowData, cell, value, colName) => {
              cell.classList.add('customer-collection-cell')
              cell.setAttribute(
                'data-customer-collection',
                rowData.customer_filter[colName],
              )

              const toggle = e => {
                if (!this.resultTable.segmentF.columns) {
                  throw new Error('Column not found')
                }
                // hit the column
                const col = this.resultTable.segmentF.columns.find(
                  c => c.colName === colName,
                )

                const id = e.target.getAttribute('data-customer-collection')
                let text

                const name = rowData.segment_f || '合計'
                if (!colName.startsWith('customer_count_ratio_')) {
                  text = name
                } else {
                  text = `${name} - ${col.headerText}`
                }

                const customer = { id, text }

                this.$emit('toggle-customer-collection', customer)
              }

              cell.addEventListener('mousedown', e => {
                hold = true
                document.body.classList.add('no-select')
                toggle(e)
              })
              cell.addEventListener('mouseup', () => {
                hold = false
                document.body.classList.remove('no-select')
              })

              cell.addEventListener('mouseenter', e => {
                if (!hold) {
                  return
                }
                toggle(e)
              })
            },
          },
        },
        segmentN: {
          columns: undefined,
          groupHeaders: undefined,
          sortColumnName: 'segment_n',
          sortOrder: 'asc',
          url: undefined,
          width: 0,
          height: 0,
          customFormatter: {
            // TODO WET
            linkify: (rowData, cell, value, colName) => {
              cell.classList.add('customer-collection-cell')
              cell.setAttribute(
                'data-customer-collection',
                rowData.customer_filter[colName],
              )

              const toggle = e => {
                if (!this.resultTable.segmentN.columns) {
                  throw new Error('Column not found')
                }
                // hit the column
                const col = this.resultTable.segmentN.columns.find(
                  c => c.colName === colName,
                )

                const id = e.target.getAttribute('data-customer-collection')
                let text

                const name = rowData.segment_n || '合計'
                if (!colName.startsWith('customer_count_ratio_')) {
                  text = name
                } else {
                  text = `${name} - ${col.headerText}`
                }

                const customer = { id, text }

                this.$emit('toggle-customer-collection', customer)
              }

              cell.addEventListener('mousedown', e => {
                hold = true
                document.body.classList.add('no-select')
                toggle(e)
              })
              cell.addEventListener('mouseup', () => {
                hold = false
                document.body.classList.remove('no-select')
              })

              cell.addEventListener('mouseenter', e => {
                if (!hold) {
                  return
                }
                toggle(e)
              })
            },
          },
        },
      },
      /**
       * 絶対採用などの選択中ダイアログ
       */
      editReport: false,
      /**
       * デンドログラム画像URL
       */
      dendroImageUrl: undefined,
      /**
       * デンドログラム画像なし理由
       */
      noImageReason: undefined,
      /**
       * 距離行列表URL
       */
      distMatrixUrl: undefined,
      /**
       * 距離行列表URL（S-JIS版）
       */
      distMatrixUrlSjis: undefined,
      /**
       * 採用順赤フォントボーダーID数構成比
       */
      customerCountRatioBorder: 1,
      /**
       * 階層ファイルアップロード中
       */
      uploadingItemFile: false,
      /**
       * この画面が持つ分析条件を、クエリのペイロードに変換する
       */
      mapQueryFilterForApi: async queryFilter => {
        if (queryFilter.item.selectMode === 'item-group-file') {
          this.uploadingItemFile = true
        }

        let itemPayload

        try {
          itemPayload = await item.mapToApiPayload(
            queryFilter.item,
            this.$itemGroup,
          )
        } catch (error) {
          throw error
        } finally {
          this.uploadingItemFile = false
        }

        const option = {
          report: queryFilter.option.report,
          repeat: queryFilter.option.repeat,
          adoptOrder: queryFilter.option.adoptOrder,
        }
        if (queryFilter.option.report === 'order') {
          option.orderValue = queryFilter.option.orderValue
        } else if (queryFilter.option.report === 'customers') {
          option.customerCountValue = queryFilter.option.customerCountValue
        }

        return {
          term: term.mapToApiPayload(queryFilter.term),
          item: itemPayload,
          store: store.mapToApiPayload(queryFilter.store),
          option,
        }
      },
      /**
       * 渡された分析条件をこの画面の分析条件に反映する
       *
       * 渡される分析条件はこの画面のものとは限らないので、"可能な限り"復元する。
       */
      applyQueryFilter: queryFilter => {
        term.applyPayload(this.queryFilter.term, queryFilter.term)
        const p = item.applyPayload(this.queryFilter.item, queryFilter.item)
        store.applyPayload(this.queryFilter.store, queryFilter.store)
        this.queryFilter.option.report = queryFilter.option.report
        this.queryFilter.option.orderValue = queryFilter.option.orderValue
        this.queryFilter.option.customerCountValue =
          queryFilter.option.customerCountValue
        this.queryFilter.option.adoptOrder = queryFilter.option.adoptOrder
        this.queryFilter.option.repeat = queryFilter.option.repeat

        return [p]
      },
      /**
       * 顧客コレクション
       */
      customerCollection: [],
      /**
       * Excel出力のパラメータ生成
       */
      excelPropertiesHandler() {
        // 現在のグリッドソート情報
        const itemSort = this.$refs.itemTable.getCurrentSort()
        const segFSort = this.$refs.segmentFTable.getCurrentSort()
        const segNSort = this.$refs.segmentNTable.getCurrentSort()

        const option = this.queryFilter.option
        const limits = {}
        if (option.report === 'order') {
          limits.limitBy =
            this.queryFilter.option.adoptOrder === 'id_count'
              ? 'customer_count_order'
              : 'coefficient_customer_count_order'
          limits.limitAt = option.orderValue
        } else if (option.report === 'customers') {
          limits.limitBy =
            this.queryFilter.option.adoptOrder === 'id_count'
              ? 'acc_customer_count_ratio_by_id_count'
              : 'acc_customer_count_ratio_by_coefficient_id_count'
          limits.limitAt = option.customerCountValue / 100
        }

        return {
          itemSortColumnName: itemSort.name,
          itemSortOrder: itemSort.order,
          adoptOrder: this.queryFilter.option.adoptOrder,
          customerCountRatioBorder: this.customerCountRatioBorder / 100,
          segFSortColumnName: segFSort.name,
          segFSortOrder: segFSort.order,
          segNSortColumnName: segNSort.name,
          segNSortOrder: segNSort.order,
          ...limits,
        }
      },
      isDetail: false,
    }
  },
  methods: {
    async loadData(queryInfo) {
      const queryFilter = this.queryFilter
      const data = JSON.parse(queryInfo.data)

      const dendrogramImage = data.kappaResult.dendrogram.image
      if (dendrogramImage.url) {
        this.dendroImageUrl = dendrogramImage.url
      } else {
        this.noImageReason = dendrogramImage.no_image_reason
      }

      const distMatrix = data.kappaResult.dendrogram.dist_matrix
      this.distMatrixUrl = distMatrix.url
      this.distMatrixUrlSjis = distMatrix.url_sjis

      const company = queryFilter.item.company

      // APIからColModelを取得
      const colModels = await Promise.all([
        repository.giggle
          .get(
            this.$longQuery.getColModelFetchUrl(
              this.resourceName,
              queryInfo.id,
              `item?company=${company}`,
            ),
          )
          .then(r => r.data),
        repository.giggle
          .get(
            this.$longQuery.getColModelFetchUrl(
              this.resourceName,
              queryInfo.id,
              `segment?company=${company}`,
            ),
          )
          .then(r => r.data),
      ])

      // 全列共通フォーマッター追加（アタッチするのはJavaScript関数でありサーバーサイドからは連携されないので）
      for (const models of colModels) {
        for (const m of models.colModel) {
          m.customFormatter = attributes
        }
      }

      const itemCol = colModels[0]
      const summaryCol = colModels[1]

      // セグメントの年代構成列は動的なので、列構成をAPIから受け取ってから
      // 詳細表示トグル用の列に追加する
      summaryCol.colModel.forEach(c => {
        if (c.colName.startsWith('customer_count_ratio')) {
          ALWAYS_SHOW_SEGMENT_COLUMNS.push(c.colName)
        }
      })

      switchAdoptOrder(queryFilter.option.adoptOrder, itemCol.colModel)

      toggleDetail(ALWAYS_SHOW_ITEM_COLUMNS, itemCol.colModel, this.isDetail)
      toggleDetail(
        ALWAYS_SHOW_SEGMENT_COLUMNS,
        summaryCol.colModel,
        this.isDetail,
      )

      this.resultTable.item.groupHeaders = itemCol.groupHeader
      this.resultTable.item.columns = itemCol.colModel

      this.resultTable.segmentF.groupHeaders = summaryCol.groupHeader
      this.resultTable.segmentF.columns = summaryCol.colModel.map(c => {
        const copy = { ...c }
        if (c.colName === 'segment_n') {
          copy.hidden = true
        }
        return copy
      })

      this.resultTable.segmentN.groupHeaders = summaryCol.groupHeader
      this.resultTable.segmentN.columns = summaryCol.colModel

      // 結果表示テーブルのURL設定
      const itemUrl = await this.composeItemDataUrl()
      const segFUrl = await this.$longQuery.getQueryResultFetchUrl(
        'tapir-mk',
        queryInfo.id,
        `segment/f?company=${company}`,
      )
      const segNUrl = await this.$longQuery.getQueryResultFetchUrl(
        'tapir-mk',
        queryInfo.id,
        `segment/n?company=${company}`,
      )

      this.resultTable.item.url = itemUrl
      this.resultTable.segmentF.url = segFUrl
      this.resultTable.segmentN.url = segNUrl
    },
    async reloadItemGrid() {
      switchAdoptOrder(
        this.queryFilter.option.adoptOrder,
        this.resultTable.item.columns,
      )
      this.$refs.itemTable.init()

      this.editReport = false
      this.resultTable.item.url = await this.composeItemDataUrl()
    },
    /**
     * 結果表示テーブルのサイズを更新する
     */
    updateTableSize(size) {
      if (sizeFixed) {
        return
      }

      const mergin = 16

      // this.$refs.myMdTabs.$el.style.width = `${size.width + mergin * 2}px`
      // this.$refs.myMdTabs.$el.style.height = `${size.height + mergin * 2}px`

      const w = size.width
      const h = size.height - mergin * 3 // TODO サイズ調整がかなり無理矢理…

      this.resultTable.item.width = w
      this.resultTable.item.height = h

      this.resultTable.segmentF.width = w
      this.resultTable.segmentF.height = h

      this.resultTable.segmentN.width = w
      this.resultTable.segmentN.height = h

      sizeFixed = true
    },
    toggleDetail(value) {
      this.isDetail = value

      this.customerCollection = []

      toggleDetail(
        ALWAYS_SHOW_ITEM_COLUMNS,
        this.resultTable.item.columns,
        value,
      )
      toggleDetail(
        ALWAYS_SHOW_SEGMENT_COLUMNS,
        this.resultTable.segmentF.columns,
        value,
      )
      toggleDetail(
        ALWAYS_SHOW_SEGMENT_COLUMNS,
        this.resultTable.segmentN.columns,
        value,
      )

      this.$refs.itemTable.init()
      this.$refs.segmentFTable.init()
      this.$refs.segmentNTable.init()
    },
    /**
     * 商品データ取得URL作成
     *
     * 複数箇所で行う & ちょっと複雑になってきたので独立
     */
    async composeItemDataUrl() {
      let urlOption = '?'
      // 採用順赤フォント設定
      urlOption += `customerCountRatioBorder=${this.customerCountRatioBorder /
        100}`

      // "明細表示形式"で絞る件数（全件表示もありえるので適用しないケースもあり）
      const option = this.queryFilter.option
      if (option.report === 'order') {
        const limitBy =
          this.queryFilter.option.adoptOrder === 'id_count'
            ? 'customer_count_order'
            : 'coefficient_customer_count_order'
        urlOption += `&limitBy=${limitBy}&limitAt=${option.orderValue}`
      } else if (option.report === 'customers') {
        const limitBy =
          this.queryFilter.option.adoptOrder === 'id_count'
            ? 'acc_customer_count_ratio_by_id_count'
            : 'acc_customer_count_ratio_by_coefficient_id_count'
        urlOption += `&limitBy=${limitBy}&limitAt=${option.customerCountValue /
          100}`
      }

      return await this.$longQuery.getQueryResultFetchUrl(
        'tapir-mk',
        this.queryId,
        'item' + urlOption,
      )
    },
  },
  mounted() {
    // MdTabsを配置すると横幅が大きくなる（左サイドバーの分？）なので、
    // 無理矢理横スクロールが出ないようにする。
    // グリッドサイズは別途合わせて調整している。
    document.querySelector('html').style.overflowX = 'hidden'
  },
}
</script>

<style lang="scss" scoped>
.radio-buttons {
  display: flex;

  &.vertical {
    flex-direction: column;
  }
}

.md-tabs {
  // 結果エリア内のタブに分析結果が入る場合、結果エリアからの
  // マージンがspacing-largeあり、タブ内がspacing-mediumある。
  // 少々マージンを取りすぎなので、グリッドまでが通常のspacing-large
  // になるように調整する。
  // node-sassのバージョン（？）のせいなのかコンパイルされたあとのCSSが
  // "calc(var(--spacing-large) - var(--spacing-medium))"となってしまっていたので
  // ひとまず固定値にした。
  // margin: calc(0px - (var(--spacing-large) - var(--spacing-medium)));
  margin: -16px;
}
</style>
